import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Container, Alert, Button, Snackbar, Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectAuth } from 'services/auth/redux/selectors'
import { getAuthPermissions, PERM_UPDATE_CONTENT } from 'services/auth'
import { getIdFromLocation } from 'services/content'
import { selectLocation } from 'services/location/redux/selectors'
import { getContentReferenceTypesContentDictionary } from 'services/content-references'
import {
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
} from 'services/content-references/types'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import Loader from 'gui/components/common/Loader'
import ModulesSection from './course-modules-section'
import CourseContentInformationSection from './CourseContentInformationSection'
import ContentReferenceSection from './content-references/ContentReferenceSection'
import {
  transformGraphQLToComponent,
  areReferencesEqual,
  compareReferences,
} from './content-references/utils'
import { GET_COURSE_CONTENT, UPDATE_COURSE_CONTENT } from './queries'
import { CREATE_CONTENT_REFERENCES, DELETE_CONTENT_REFERENCE } from './content-references/queries'
import ContentMediaSection from './content-detail-media/content-media-section'
import ContentImagesSection from './content-detail-images/content-images-section'
import type {
  ContentReference,
  ContentReferenceMap,
  ContentInfoChanges,
  SnackbarState,
  CourseModuleReference,
} from './types'
import './course-detail.scss'

// Define which content reference types Course supports
const COURSE_CONTENT_REFERENCE_TYPES = [
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
]

interface SaveContentReferencesResult {
  success: boolean
  message: string
  error?: string
}

/**
 * Course Detail Component
 * Displays and allows editing of Course content with Modules
 *
 * This component uses Apollo Client for data fetching (modern YI pattern)
 * No Redux for content data - only for auth and location
 */
function CourseDetail () {
  const auth = useSelector(selectAuth)
  const location = useSelector(selectLocation)
  const authPermissions = getAuthPermissions(auth)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)

  // Get content ID from URL
  const id = getIdFromLocation(location)

  // Fetch course data from GraphQL
  const { loading, error, data, refetch } = useQuery(GET_COURSE_CONTENT, {
    variables: { id },
    skip: !id,
  })

  // Get data from GraphQL response as plain object
  const courseData = useMemo(() => data?.contentList?.content?.[0] || null, [data])

  // Extract course modules from the data
  const modules = useMemo<CourseModuleReference[]>(
    () => courseData?.contentReferencesTo || [],
    [courseData],
  )

  // Extract and transform content references from the data
  // Group by reference type for easier state management
  const initialContentReferences: ContentReferenceMap = useMemo(() => {
    const referencesFrom = courseData?.contentReferencesFrom
    if (!referencesFrom) return {}

    const allReferences = transformGraphQLToComponent(referencesFrom)

    // Group references by type
    const grouped: ContentReferenceMap = {}
    COURSE_CONTENT_REFERENCE_TYPES.forEach((refType) => {
      grouped[refType] = allReferences.filter((ref) => ref.contentReferenceType === refType)
    })

    return grouped
  }, [courseData])

  // Track content references state - keyed by reference type
  const [contentReferences, setContentReferences] = useState<ContentReferenceMap>({})

  // Track content references dirty state
  const [contentReferencesDirty, setContentReferencesDirty] = useState<boolean>(false)

  // Initialize contentReferences when data loads
  useEffect(() => {
    setContentReferences(initialContentReferences)
  }, [initialContentReferences])

  // Recalculate dirty state when initialContentReferences or contentReferences change
  // This handles the case where refetch updates the initial values after save
  useEffect(() => {
    const hasChanged = COURSE_CONTENT_REFERENCE_TYPES.some((refType) => {
      const initial = initialContentReferences[refType] || []
      const current = contentReferences[refType] || []
      return !areReferencesEqual(initial, current)
    })
    setContentReferencesDirty(hasChanged)
  }, [initialContentReferences, contentReferences])

  // Track dirty state from Content Information section
  const [contentInfoDirty, setContentInfoDirty] = useState<boolean>(false)

  // Store changed values from Content Information section
  const [contentInfoChanges, setContentInfoChanges] = useState<ContentInfoChanges | null>(null)

  // Track dirty state from Media section
  const [mediaDirty, setMediaDirty] = useState<boolean>(false)
  const [mediaChanges, setMediaChanges] = useState<ContentInfoChanges | null>(null)

  // Track dirty state from Modules section
  const [modulesDirty, setModulesDirty] = useState<boolean>(false)

  // Store the save handler from Modules section
  const [modulesSaveHandler, setModulesSaveHandler] = useState<(() => Promise<void>) | null>(null)

  // Track reset trigger counter
  const [resetTrigger, setResetTrigger] = useState<number>(0)

  // Overall dirty state (includes content info AND media AND content references AND modules)
  const isDirty = contentInfoDirty || mediaDirty || contentReferencesDirty || modulesDirty

  // Handle content reference changes for a specific reference type
  const handleContentReferencesChange = useCallback(
    (referenceType: string, newReferences: ContentReference[]) => {
      setContentReferences((prev) => ({
        ...prev,
        [referenceType]: newReferences,
      }))

      // Check if ANY reference type has changed from initial
      const updatedRefs = {
        ...contentReferences,
        [referenceType]: newReferences,
      }

      const hasChanged = COURSE_CONTENT_REFERENCE_TYPES.some((refType) => {
        const initial = initialContentReferences[refType] || []
        const current = updatedRefs[refType] || []
        return !areReferencesEqual(initial, current)
      })

      setContentReferencesDirty(hasChanged)
    },
    [contentReferences, initialContentReferences],
  )

  // Handle dirty change from Content Information
  const handleContentInfoDirtyChange = useCallback((isContentDirty: boolean) => {
    setContentInfoDirty(isContentDirty)
  }, [])

  // Handle changes update from Content Information
  const handleContentInfoChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setContentInfoChanges(changes)
  }, [])

  // Handle dirty change from Media section
  const handleMediaDirtyChange = useCallback((isMediaDirty: boolean) => {
    setMediaDirty(isMediaDirty)
  }, [])

  // Handle changes update from Media section
  const handleMediaChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setMediaChanges(changes)
  }, [])

  // Handle dirty change from Modules
  const handleModulesDirtyChange = useCallback((isStepsDirty: boolean) => {
    setModulesDirty(isStepsDirty)
  }, [])

  // Handle save handler ready from Modules
  const handleModulesSaveHandlerReady = useCallback((saveHandler: () => Promise<void>) => {
    setModulesSaveHandler(() => saveHandler)
  }, [])

  // Mutation for updating course content
  const [updateCourseContent, { loading: saving }] = useMutation(UPDATE_COURSE_CONTENT, {
    refetchQueries: [{ query: GET_COURSE_CONTENT, variables: { id } }],
    awaitRefetchQueries: true,
  })

  // Mutation for creating content references (batch)
  const [createContentReferences] = useMutation(CREATE_CONTENT_REFERENCES)

  // Mutation for deleting content references (single)
  const [deleteContentReference] = useMutation(DELETE_CONTENT_REFERENCE)

  // Snackbar state for success/error messages
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Handle saving content references
  const handleSaveContentReferences =
    useCallback(async (): Promise<SaveContentReferencesResult> => {
      // Collect ALL current references and deletions per reference type
      // Following YI pattern: send ALL current references to create (mutation is idempotent)
      const allCurrentReferences: ContentReference[] = []
      const allDeletions: ContentReference[] = []

      COURSE_CONTENT_REFERENCE_TYPES.forEach((refType) => {
        const current = contentReferences[refType] || []
        const initial = initialContentReferences[refType] || []

        const { deletions } = compareReferences(initial, current)

        // Tag ALL current references with their reference type
        current.forEach((item) => {
          allCurrentReferences.push({
            ...item,
            contentReferenceType: refType, // Ensure type is set
          })
        })

        allDeletions.push(...deletions)
      })

      // If no current references and no deletions, return early
      if (allCurrentReferences.length === 0 && allDeletions.length === 0) {
        return { success: true, message: 'No content references to save' }
      }

      try {
        // Delete references FIRST (before creating)
        if (allDeletions.length > 0) {
          // Delete all references in parallel
          // Note: ContentReferenceDeleteInput only requires id field
          await Promise.all(
            allDeletions.map((item) =>
              deleteContentReference({
                variables: {
                  contentReference: {
                    id: item.referenceId, // Only field needed for deletion
                  },
                },
              }),
            ),
          )
        }

        // Create ALL current references (batch)
        // The mutation is idempotent - it will skip existing references
        if (allCurrentReferences.length > 0) {
          // Build mutation variables for batch create
          const contentReferencesToCreate = allCurrentReferences.map((item) => ({
            fromContentContentId: courseData?.contentId,
            fromContentContentType: courseData?.contentType,
            toContentContentId: item.contentId,
            toContentContentType: item.contentType,
            type: item.contentReferenceType, // e.g., "admin_category"
            sortKey: null, // Not needed for MVP
          }))

          await createContentReferences({
            variables: {
              contentReferences: contentReferencesToCreate,
            },
          })
        }

        return {
          success: true,
          message: `Successfully saved content references: ${allCurrentReferences.length} total, ${allDeletions.length} deleted`,
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        return {
          success: false,
          error: errorMessage,
          message: `Error saving content references: ${errorMessage}`,
        }
      }
    }, [
      contentReferences,
      initialContentReferences,
      courseData,
      createContentReferences,
      deleteContentReference,
    ])

  // Handle save
  const handleSave = async () => {
    try {
      // Check if there are any changes to save
      if (!contentInfoChanges && !mediaChanges && !contentReferencesDirty && !modulesDirty) {
        setSnackbar({
          open: true,
          message: 'No changes to save',
          severity: 'info',
        })
        return
      }

      // Merge content info changes and media changes into a single update
      // If both have changes, merge them; otherwise use whichever has changes
      let combinedChanges: ContentInfoChanges | null = null

      if (contentInfoChanges && mediaChanges) {
        // Merge both changes
        combinedChanges = {
          contentInput: {
            ...contentInfoChanges.contentInput,
            ...mediaChanges.contentInput,
          },
          generatePath: contentInfoChanges.generatePath || mediaChanges.generatePath || false,
        }
      } else if (contentInfoChanges) {
        combinedChanges = contentInfoChanges
      } else if (mediaChanges) {
        combinedChanges = mediaChanges
      }

      // Save content info and/or media if changed
      if (combinedChanges) {
        await updateCourseContent({
          variables: {
            content: combinedChanges.contentInput,
            generatePath: combinedChanges.generatePath,
          },
        })
      }

      // Save content references if changed
      if (contentReferencesDirty) {
        const result = await handleSaveContentReferences()
        if (!result.success) {
          throw new Error(result.message)
        }
      }

      // Save modules reorder if changed
      if (modulesDirty && modulesSaveHandler) {
        await modulesSaveHandler()
      }

      // Refetch course data to get updated content references
      await refetch()

      // Clear dirty flags after successful save and refetch
      setContentReferencesDirty(false)

      // Show success message
      setSnackbar({
        open: true,
        message: 'Course saved successfully!',
        severity: 'success',
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setSnackbar({
        open: true,
        message: `Error saving: ${errorMessage}`,
        severity: 'error',
      })
    }
  }

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  // Handle cancel - trigger reset in child
  const handleCancel = () => {
    // Reset content references to initial values
    setContentReferences(initialContentReferences)
    setContentReferencesDirty(false)

    // Trigger reset in child components
    setResetTrigger((prev) => prev + 1)
    setSnackbar({
      open: true,
      message: 'Changes discarded',
      severity: 'info',
    })
  }

  // Loading state
  if (loading) {
    return <Loader />
  }

  // Error state
  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 3 }}>
          <div>
            <strong>Error loading Course</strong>
          </div>
          <div>{error.message}</div>
        </Alert>
      </Container>
    )
  }

  // Not found state
  if (!courseData && !loading) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 3 }}>
          <div>
            <strong>Course not found</strong>
          </div>
          <div>No Course found with ID: {id}</div>
        </Alert>
      </Container>
    )
  }

  return (
    <Container className="course-detail content-detail" maxWidth={false}>
      {/* Content Information Section */}
      <Container className="content-detail__section content-information" maxWidth={false}>
        <CourseContentInformationSection
          contentData={courseData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={handleContentInfoDirtyChange}
          onChangesUpdate={handleContentInfoChangesUpdate}
          onResetTriggered={resetTrigger}
        />
      </Container>

      {/* Course Modules Section */}
      <Container className="content-detail__section course-modules" maxWidth={false}>
        <SectionAccordion title="Course Modules" collapsed={false}>
          <ModulesSection
            modules={modules}
            disabled={isViewOnlyMode}
            courseLanguage={courseData?.language}
            courseContentId={courseData?.contentId}
            courseContentType={courseData?.contentType}
            courseId={courseData?.id}
            onDirtyChange={handleModulesDirtyChange}
            onSaveHandlerReady={handleModulesSaveHandlerReady}
            onResetTriggered={resetTrigger}
          />
        </SectionAccordion>
      </Container>

      {/* Content References Section */}
      <Container className="content-detail__section content-references" maxWidth={false}>
        <SectionAccordion title="Content References" collapsed={false}>
          {COURSE_CONTENT_REFERENCE_TYPES.map((referenceType) => {
            // Get configuration from service dictionary (still uses Immutable.js)
            // This will be typed as `any` until we convert the service layer
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const config: any = getContentReferenceTypesContentDictionary().get(referenceType)
            if (!config) return null

            return (
              <ContentReferenceSection
                key={referenceType}
                label={config.get('label')}
                value={contentReferences[referenceType] || []}
                onChange={(newReferences) =>
                  handleContentReferencesChange(referenceType, newReferences)
                }
                disabled={isViewOnlyMode}
                contentType={config.get('contentType')?.first?.() || config.get('contentType')}
                bundle={config.get('bundle')}
                filterType={config.get('filterType')}
              />
            )
          })}
        </SectionAccordion>
      </Container>

      {/* Hovering Save and Cancel Buttons - Only appear when there are changes */}
      {!isViewOnlyMode && (
        <Box
          className="content-detail__action-buttons"
          sx={{
            position: 'fixed',
            bottom: isDirty ? '20px' : '-100px',
            right: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.3s ease',
            opacity: isDirty ? 1 : 0,
            pointerEvents: isDirty ? 'auto' : 'none',
            minWidth: '200px',
          }}
        >
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              sx={{
                flex: 1,
                minWidth: '100px',
                height: '42px',
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
              sx={{
                flex: 1,
                minWidth: '100px',
                height: '42px',
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Future sections can go here */}
      {/* TODO: Add Content Policy section */}

      <Container className="content-detail__section media-information" maxWidth={false}>
        <SectionAccordion title="Media Information" collapsed={false}>
          <ContentMediaSection
            contentData={courseData}
            isViewOnly={isViewOnlyMode}
            onDirtyChange={handleMediaDirtyChange}
            onChangesUpdate={handleMediaChangesUpdate}
            onResetTriggered={resetTrigger}
          />
        </SectionAccordion>
      </Container>

      <Container className="content-detail__section content-images" maxWidth={false}>
        <SectionAccordion title="Images" collapsed={false}>
          <ContentImagesSection
            contentId={courseData?.contentId}
            contentType={courseData?.contentType}
            language={courseData?.language}
            primaryLanguage={courseData?.primaryLanguage}
          />
        </SectionAccordion>
      </Container>
    </Container>
  )
}

export default CourseDetail
